import { escapeHtml, printHtmlDocument } from './printHtmlDocument'
import { getEcddPreviewSections } from '../pages/compliance-safe/components/ecddPreviewContent'

function sectionHtml(section) {
  const status =
    section.ok != null
      ? `<span class="status ${section.ok ? 'ok' : 'warn'}">${section.ok ? 'Complete' : 'Incomplete'}</span>`
      : ''
  const rows = (section.rows || [])
    .map(
      (r) =>
        `<div class="row"><dt>${escapeHtml(r.label)}</dt><dd>${escapeHtml(r.value)}</dd></div>`
    )
    .join('')
  if (!rows) return ''
  return `<section>
    <h2><span class="num">${escapeHtml(String(section.number))}</span> ${escapeHtml(section.title)} ${status}</h2>
    <dl class="rows">${rows}</dl>
  </section>`
}

/**
 * Print ECDD preview (read-only form summary) — same content as EcddDocumentPreviewModal.
 */
export function printEcddPreview({
  record,
  formAnswers,
  decision,
  decisionRationale,
  gates,
  onError,
} = {}) {
  const { sections, evalGates } = getEcddPreviewSections({
    record,
    formAnswers,
    decision,
    decisionRationale,
    gates,
  })
  const patron = record?.name || 'Patron'
  const ecddId = record?.id || '—'
  const sectionsHtml = sections.map(sectionHtml).join('')
  const footerNote = evalGates.allOk
    ? 'All required form sections are complete.'
    : 'Some required sections are still incomplete.'

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>ECDD ${escapeHtml(ecddId)}</title>
  <style>
    @page { margin: 14mm; }
    * { box-sizing: border-box; }
    body { font-family: system-ui, -apple-system, sans-serif; color: #1e293b; margin: 0; padding: 20px; line-height: 1.45; font-size: 13px; }
    .brand { font-size: 11px; letter-spacing: 0.12em; text-transform: uppercase; color: #0d7377; font-weight: 700; }
    h1 { font-size: 20px; margin: 6px 0 2px; color: #073b4d; }
    .sub { color: #64748b; font-size: 12px; margin-bottom: 16px; }
    section { margin-bottom: 14px; page-break-inside: avoid; border: 1px solid #e2e8f0; border-radius: 8px; overflow: hidden; }
    section h2 { font-size: 12px; margin: 0; padding: 8px 12px; background: #f8fafc; border-bottom: 1px solid #e2e8f0; color: #073b4d; display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
    section h2 .num { display: inline-flex; width: 20px; height: 20px; border-radius: 999px; background: #0d7377; color: #fff; font-size: 10px; align-items: center; justify-content: center; }
    .status { font-size: 10px; font-weight: 600; padding: 2px 8px; border-radius: 999px; margin-left: auto; }
    .status.ok { background: #f0fdf4; color: #15803d; }
    .status.warn { background: #fffbeb; color: #b45309; }
    .rows { margin: 0; padding: 8px 12px; }
    .row { display: grid; grid-template-columns: 140px 1fr; gap: 8px; padding: 6px 0; border-bottom: 1px solid #f1f5f9; }
    .row:last-child { border-bottom: 0; }
    dt { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.04em; color: #94a3b8; margin: 0; }
    dd { margin: 0; white-space: pre-wrap; word-break: break-word; font-size: 13px; color: #1e293b; }
    .summary { margin-top: 12px; padding: 10px 12px; border-radius: 8px; font-size: 12px; }
    .summary.ok { background: #f0fdf4; border: 1px solid #bbf7d0; color: #15803d; }
    .summary.warn { background: #fffbeb; border: 1px solid #fde68a; color: #b45309; }
    .footer { margin-top: 20px; padding-top: 8px; border-top: 1px solid #e2e8f0; font-size: 11px; color: #94a3b8; }
    @media print { body { padding: 0; } }
  </style>
</head>
<body>
  <div class="brand">PaySafeGo · ECDD preview</div>
  <h1>${escapeHtml(patron)} · ${escapeHtml(ecddId)}</h1>
  <p class="sub">Read-only summary of recorded selections — not the working form.</p>
  ${sectionsHtml}
  <p class="summary ${evalGates.allOk ? 'ok' : 'warn'}">${escapeHtml(footerNote)}</p>
  <p class="footer">Printed ${escapeHtml(new Date().toLocaleString())} from PaySafeGo ECDD.</p>
</body>
</html>`

  printHtmlDocument(html, { onError: onError || (() => {}) })
}
